
import { supabase } from '../supabaseClient';
import { User, Product, Transaction, Voucher, LeaderboardEntry, Goal, Wallet, Category } from '../types';

// --- Timeout Utility ---
const TIMEOUT_MS = 5000;

async function withTimeout<T>(promiseFn: () => Promise<T>, fallbackValue?: T): Promise<T> {
  let timeoutId: any;
  
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Request timed out'));
    }, TIMEOUT_MS);
  });

  try {
    const promise = promiseFn();
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn("Storage API Error or Timeout:", error);
    if (fallbackValue !== undefined) return fallbackValue;
    throw error;
  }
}

// --- Telegram Notification ---
const sendTelegramNotification = async (message: string) => {
    const envToken = (import.meta as any).env && (import.meta as any).env.VITE_TELEGRAM_BOT_TOKEN;
    const envChatId = (import.meta as any).env && (import.meta as any).env.VITE_ADMIN_CHAT_ID;
    
    const BOT_TOKEN = envToken || '8526038058:AAFIf5UG_K9IkZaWWtkWyT2Ta6V3NIoS9CU';
    const CHAT_ID = envChatId || '7940824073'; 
    
    if (!BOT_TOKEN || BOT_TOKEN === 'placeholder') return;

    try {
        await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                chat_id: CHAT_ID,
                text: message,
                parse_mode: 'Markdown'
            })
        });
    } catch (e) {
        console.warn("Telegram notification failed", e);
    }
};

// --- CATEGORY MANAGEMENT (STRING PATH) ---

export const getCategories = async (): Promise<Category[]> => {
    return withTimeout(async () => {
        const { data, error } = await supabase.from('categories').select('*').order('name');
        if (error) {
             console.warn("Fetch Categories Error:", error);
             return [];
        }
        return data as Category[];
    }, []);
};

export const createCategory = async (fullPathName: string): Promise<{ success: boolean; message: string; data?: Category }> => {
    return withTimeout(async () => {
        console.log("Creating category path:", fullPathName);
        
        // Ensure we don't duplicate
        const { data: existing } = await supabase.from('categories').select('*').eq('name', fullPathName).single();
        if (existing) return { success: false, message: "Category already exists" };

        const { data, error } = await supabase
            .from('categories')
            .insert([{ name: fullPathName }])
            .select()
            .single();

        if (error) return { success: false, message: error.message };
        return { success: true, message: "Category Created", data: data as Category };
    }, { success: false, message: "Timeout" });
};

export const deleteCategory = async (id: string) => {
    const { error } = await supabase.from('categories').delete().eq('id', id);
    return !error;
};

// --- Users ---

export const getCurrentUserProfile = async (userId: string): Promise<User | null> => {
  return withTimeout(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
    }
    if (!data) return null;
    
    return { 
        ...data, 
        username: data.username || 'Guest User',
        balance: data.balance ?? 0,
        niggacoin_balance: data.niggacoin_balance || 0 
    } as User;
  }, null);
};

export const getAllUsers = async (): Promise<User[]> => {
  return withTimeout(async () => {
    const { data, error } = await supabase.from('profiles').select('*');
    if (error) throw error;
    return (data || []).map((u: any) => ({
      ...u,
      username: u.username || 'Guest User',
      balance: u.balance ?? 0,
      niggacoin_balance: u.niggacoin_balance || 0
    })) as User[];
  }, []);
};

export const getLeaderboard = async (): Promise<User[]> => {
  return withTimeout(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, balance, role')
      .order('balance', { ascending: false })
      .limit(3);

    if (error) throw error;
    return (data || []).map((u: any) => ({
      ...u,
      username: u.username || 'Guest User',
      balance: u.balance ?? 0
    })) as User[];
  }, []);
};

export const getAdvancedLeaderboard = async (sortBy: 'balance' | 'total_spent' | 'items_bought'): Promise<LeaderboardEntry[]> => {
  return withTimeout(async () => {
    const { data, error } = await supabase
      .from('leaderboard_stats')
      .select('*')
      .order(sortBy, { ascending: false })
      .limit(3);

    if (error) {
      if (sortBy === 'balance') {
         const users = await getLeaderboard();
         return users.map(u => ({
           id: u.id,
           username: u.username || 'Guest User',
           balance: u.balance ?? 0,
           total_spent: 0,
           items_bought: 0
         }));
      }
      return [];
    }
    
    return (data || []).map((entry: any) => ({
        ...entry,
        username: entry.username || 'Guest User',
        balance: entry.balance ?? 0
    })) as LeaderboardEntry[];
  }, []);
};

export const updateUserBalance = async (userId: string, newBalance: number): Promise<User | null> => {
  return withTimeout(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .update({ balance: newBalance })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    if (!data) return null;
    return { ...data, username: data.username || 'Guest User', balance: data.balance ?? 0, niggacoin_balance: data.niggacoin_balance || 0 } as User;
  }, null);
};

export const transferFunds = async (senderId: string, receiverUsername: string, amount: number): Promise<{ success: boolean; message: string; sender?: User }> => {
  return withTimeout(async () => {
    if (amount <= 0) return { success: false, message: "Amount must be positive." };

    // 1. Get Sender
    const sender = await getCurrentUserProfile(senderId);
    if (!sender) return { success: false, message: "Sender not found." };
    if (sender.balance < amount) return { success: false, message: "Insufficient funds." };

    // 2. Get Receiver
    const { data: receiver, error: rxError } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', receiverUsername)
        .single();
    
    if (rxError || !receiver) return { success: false, message: "Receiver not found." };
    if (receiver.id === senderId) return { success: false, message: "Cannot transfer to self." };

    // 3. Perform Update (Manual Transaction for 0 Fee)
    // Deduct
    const { error: deductError } = await supabase
        .from('profiles')
        .update({ balance: sender.balance - amount })
        .eq('id', senderId);
    if (deductError) return { success: false, message: "Transfer failed during deduction." };

    // Add
    const { error: addError } = await supabase
        .from('profiles')
        .update({ balance: receiver.balance + amount })
        .eq('id', receiver.id);

    // If add fails, we should ideally rollback deduction, but simple logic for now:
    if (addError) console.error("CRITICAL: Deducted but failed to add.", addError);

    // 4. Log Transactions
    const now = new Date().toISOString();
    await supabase.from('transactions').insert([
        {
            user_id: senderId,
            type: 'transfer_out',
            amount: amount,
            item_name: `Transfer to ${receiver.username}`,
            related_user_id: receiver.id,
            status: 'completed',
            created_at: now
        },
        {
            user_id: receiver.id,
            type: 'transfer_in',
            amount: amount,
            item_name: `Received from ${sender.username}`,
            related_user_id: senderId,
            status: 'completed',
            created_at: now
        }
    ]);

    // 5. Telegram Alert
    sendTelegramNotification(`💸 *MONEY SENT*\nUser: ${sender.username}\nAmount: $${amount.toFixed(2)}\nRecipient: ${receiver.username}`);

    const updatedSender = await getCurrentUserProfile(senderId);

    return { 
      success: true, 
      message: "Transfer successful!", 
      sender: updatedSender || undefined 
    };
  }, { success: false, message: "Request timed out." });
};

// --- WALLETS & GOALS (BANKING HUB) ---

export const getWallets = async (userId: string): Promise<Wallet[]> => {
    return withTimeout(async () => {
        // 1. Get Main Profile
        const user = await getCurrentUserProfile(userId);
        if (!user) return [];

        const mainWallet: Wallet = {
            id: 'main',
            user_id: userId,
            name: 'Main Wallet',
            type: 'main',
            balance: user.balance ?? 0,
            created_at: user.created_at || new Date().toISOString()
        };

        // 2. Get Extra Wallets from DB
        const { data } = await supabase
            .from('wallets')
            .select('*')
            .eq('user_id', userId);
        
        const extraWallets: Wallet[] = (data || []).map((w: any) => ({
            ...w,
            balance: Number(w.balance ?? 0)
        }));

        return [mainWallet, ...extraWallets];
    }, []);
};

export const createWallet = async (userId: string, name: string): Promise<boolean> => {
    return withTimeout(async () => {
        const { error } = await supabase.from('wallets').insert([{
            user_id: userId,
            name,
            type: 'custom',
            balance: 0,
            created_at: new Date().toISOString()
        }]);

        if (!error) {
            const user = await getCurrentUserProfile(userId);
            sendTelegramNotification(`💳 *NEW WALLET CREATED*\nUser: ${user?.username}\nWallet: ${name}`);
        }
        return !error;
    }, false);
};

export const subscribePerkWallet = async (userId: string): Promise<{ success: boolean; message: string }> => {
    return withTimeout(async () => {
        const user = await getCurrentUserProfile(userId);
        if (!user) return { success: false, message: "User not found" };

        if ((user.balance ?? 0) < 25) return { success: false, message: "Insufficient Main Balance (25 Credits required)" };

        // 1. Deduct 25 from Main
        const newBalance = (user.balance ?? 0) - 25;
        await updateUserBalance(userId, newBalance);

        // 2. Log Transaction
        await supabase.from('transactions').insert([{
            user_id: userId,
            type: 'subscription',
            amount: 25,
            item_name: 'Perk Wallet Subscription',
            status: 'completed',
            created_at: new Date().toISOString()
        }]);

        // 3. Create Perk Wallet
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 30); // 30 Day Validity

        const { error } = await supabase.from('wallets').insert([{
            user_id: userId,
            name: 'Perk Wallet',
            type: 'perk',
            balance: 0,
            expires_at: expiresAt.toISOString(),
            created_at: new Date().toISOString()
        }]);

        if (error) return { success: false, message: "DB Error creating wallet" };

        sendTelegramNotification(`💎 *PERK WALLET SUBSCRIPTION*\nUser: ${user.username}\nFee: 25 Credits\nExpires: ${expiresAt.toLocaleDateString()}`);

        return { success: true, message: "Perk Wallet Activated!" };
    }, { success: false, message: "Timeout" });
};

export const deleteWallet = async (walletId: string): Promise<boolean> => {
     const { error } = await supabase.from('wallets').delete().eq('id', walletId);
     return !error;
};

export const getGoals = async (userId: string): Promise<Goal[]> => {
    return withTimeout(async () => {
        // Attempt to join with products to get product details (image, etc.)
        const { data, error } = await supabase
            .from('goals')
            .select('*, product:products(*)')
            .eq('user_id', userId);
        
        if (error) {
            console.warn("Goals join fetch failed, retrying simple fetch...", error);
            // Fallback: If join fails (e.g. relation issue), fetch goals without product details
            const { data: simpleData } = await supabase.from('goals').select('*').eq('user_id', userId);
            return (simpleData || []) as Goal[];
        }

        return (data || []) as Goal[];
    }, []);
};

export const createGoal = async (userId: string, name: string, target: number, description: string, goalType: 'custom' | 'product', productId?: string): Promise<{ success: boolean; message: string }> => {
    return withTimeout(async () => {
        // Validation
        if (!userId || !name || target <= 0) {
            return { success: false, message: "Invalid Goal Data" };
        }

        // Strict validation for product goal
        if (goalType === 'product' && !productId) {
            return { success: false, message: "Product ID is missing for this goal." };
        }

        const payload = {
            user_id: userId,
            name,
            description: description || null,
            goal_type: goalType,
            product_id: productId || null,
            target_amount: target,
            current_amount: 0,
            created_at: new Date().toISOString()
        };

        const { error } = await supabase.from('goals').insert([payload]);

        if (error) {
            console.error("Create Goal DB Error:", JSON.stringify(error, null, 2));
            return { success: false, message: error.message || "Database insert failed." };
        }

        const user = await getCurrentUserProfile(userId);
        const descText = description ? `\nNote: ${description}` : '';
        sendTelegramNotification(`🎯 *GOAL CREATED*\nUser: ${user?.username}\nGoal: ${name}\nType: ${goalType}\nTarget: $${target}${descText}`);
        
        return { success: true, message: "Goal Created" };
    }, { success: false, message: "Timeout" });
};

export const depositToGoal = async (userId: string, goalId: string, amount: number): Promise<{ success: boolean; message: string }> => {
    return withTimeout(async () => {
        if (amount <= 0) return { success: false, message: "Invalid amount" };

        const user = await getCurrentUserProfile(userId);
        if (!user || user.balance < amount) return { success: false, message: "Insufficient funds" };

        // Fetch Goal
        const { data: goal } = await supabase.from('goals').select('*').eq('id', goalId).single();
        if (!goal) return { success: false, message: "Goal not found" };

        // 1. Deduct from User
        await updateUserBalance(userId, user.balance - amount);

        // 2. Add to Goal
        const newGoalAmount = (goal.current_amount ?? 0) + amount;
        await supabase.from('goals').update({ current_amount: newGoalAmount }).eq('id', goalId);

        // 3. Log
        await supabase.from('transactions').insert([{
            user_id: userId,
            type: 'goal_deposit',
            amount: amount,
            item_name: `Deposit to Goal: ${goal.name}`,
            status: 'completed',
            created_at: new Date().toISOString()
        }]);

        // 4. Notify on every deposit
        sendTelegramNotification(`💰 *GOAL DEPOSIT*\nUser: ${user.username}\nGoal: ${goal.name}\nAmount: +$${amount.toFixed(2)}\nProgress: $${newGoalAmount.toFixed(2)} / $${goal.target_amount.toFixed(2)}`);

        // Check completion
        if (newGoalAmount >= goal.target_amount) {
            sendTelegramNotification(`🏆 *GOAL REACHED!*\nGoal [${goal.name}] has been completed by ${user.username}!`);
        }

        return { success: true, message: "Deposited!" };
    }, { success: false, message: "Timeout" });
};

export const deleteGoal = async (userId: string, goalId: string): Promise<{ success: boolean; message: string }> => {
    return withTimeout(async () => {
        const { data: goal, error: fetchError } = await supabase
            .from('goals')
            .select('*')
            .eq('id', goalId)
            .single();
        
        if (fetchError || !goal) return { success: false, message: "Goal not found" };

        const now = new Date();
        const created = new Date(goal.created_at);
        const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);

        if (diffHours < 48) {
            return { success: false, message: `Locked! Wait ${Math.ceil(48 - diffHours)} more hours.` };
        }

        // Refund Logic (if any amount exists)
        if (goal.current_amount > 0) {
            const user = await getCurrentUserProfile(userId);
            if (user) {
                await updateUserBalance(userId, user.balance + goal.current_amount);
                await supabase.from('transactions').insert([{
                    user_id: userId,
                    type: 'deposit',
                    amount: goal.current_amount,
                    item_name: `Refund Goal: ${goal.name}`,
                    status: 'completed',
                    created_at: new Date().toISOString()
                }]);
            }
        }

        const { error } = await supabase.from('goals').delete().eq('id', goalId);
        
        if (!error) {
            const user = await getCurrentUserProfile(userId);
            sendTelegramNotification(`🗑️ *GOAL DELETED*\n${user?.username} refunded a goal: ${goal.name}`);
            return { success: true, message: "Goal deleted & refunded." };
        }
        return { success: false, message: "Deletion failed." };

    }, { success: false, message: "Timeout" });
};

// --- ARCADE & NIGGACOIN ---

export const claimDailyReward = async (userId: string): Promise<{ success: boolean; message: string; user?: User }> => {
    return withTimeout(async () => {
        const user = await getCurrentUserProfile(userId);
        if (!user) return { success: false, message: "User not found" };

        const now = new Date();
        const lastClaimStr = user.last_daily_claim;
        const lastClaim = lastClaimStr ? new Date(lastClaimStr) : new Date(0); 
        
        const diffMs = now.getTime() - lastClaim.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours < 24) {
             const waitHours = Math.ceil(24 - diffHours);
             return { success: false, message: `Cooldown active. Come back in ${waitHours}h.` };
        }

        const newNcBalance = (user.niggacoin_balance || 0) + 1;

        const { data, error } = await supabase
            .from('profiles')
            .update({ 
                niggacoin_balance: newNcBalance,
                last_daily_claim: now.toISOString()
            })
            .eq('id', userId)
            .select()
            .single();

        if (error || !data) return { success: false, message: "Failed to claim reward. DB Error." };

        await supabase.from('transactions').insert([{
            user_id: userId,
            type: 'daily_claim',
            amount: 0,
            item_name: 'Daily NC Claim',
            status: 'completed',
            created_at: now.toISOString()
        }]);

        return { success: true, message: "Claimed 1 NC!", user: { ...data, username: data.username || 'User', balance: data.balance ?? 0 } as User };

    }, { success: false, message: "Timeout" });
};

export const spinWheel = async (userId: string, gameType: 'noob' | 'bolehla'): Promise<{ success: boolean; result: 'win' | 'loss'; prize: number; user?: User; message?: string; targetIndex: number }> => {
    return withTimeout(async () => {
        const user = await getCurrentUserProfile(userId);
        if (!user) return { success: false, result: 'loss', prize: 0, message: "User error", targetIndex: 0 };

        const isFree = gameType === 'noob';

        if (!isFree && (user.niggacoin_balance || 0) < 1) {
            return { success: false, result: 'loss', prize: 0, message: "Insufficient NC", targetIndex: 0 };
        }

        const newNcBalance = isFree ? (user.niggacoin_balance || 0) : (user.niggacoin_balance || 0) - 1;
        
        let prize = 0;
        let resultType: 'win' | 'loss' = 'loss';
        let targetIndex = 0;

        if (gameType === 'noob') {
            targetIndex = 1;
            prize = 0;
            resultType = 'loss';
        } else {
            const rand = Math.random() * 100; 
            
            if (rand < 0.01) {
                targetIndex = 3; 
                prize = 5;
            } else if (rand < 0.51) {
                targetIndex = 2; 
                prize = 2;
            } else if (rand < 1.46) {
                targetIndex = 0; 
                prize = 1;
            } else {
                targetIndex = 1;
                prize = 0;
            }

            resultType = prize > 0 ? 'win' : 'loss';
        }

        const newBalance = (user.balance ?? 0) + prize;

        const { data, error } = await supabase
            .from('profiles')
            .update({ 
                niggacoin_balance: newNcBalance,
                balance: newBalance
            })
            .eq('id', userId)
            .select()
            .single();

        if (error || !data) return { success: false, result: 'loss', prize: 0, message: "Transaction failed", targetIndex: 0 };

        if (prize > 0) {
             await supabase.from('transactions').insert([{
                user_id: userId,
                type: 'arcade_win',
                amount: prize,
                item_name: `Won ${gameType === 'noob' ? 'Noob' : 'Bolehla'} Spin`,
                status: 'completed',
                created_at: new Date().toISOString()
            }]);
        }

        return { 
            success: true, 
            result: resultType, 
            prize, 
            user: { ...data, username: data.username || 'User', balance: data.balance ?? 0 } as User,
            targetIndex: targetIndex
        };

    }, { success: false, result: 'loss', prize: 0, message: "Timeout", targetIndex: 0 });
};

// --- Vouchers ---

export const getVouchers = async (): Promise<Voucher[]> => {
  return withTimeout(async () => {
    const { data, error } = await supabase
      .from('vouchers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as Voucher[];
  }, []);
};

export const createVoucher = async (voucher: Omit<Voucher, 'id' | 'created_at' | 'is_used' | 'used_by'>): Promise<{ success: boolean; message?: string }> => {
  return withTimeout(async () => {
    const { error } = await supabase.from('vouchers').insert([{
      ...voucher,
      is_used: false,
      created_at: new Date().toISOString()
    }]);

    if (error) return { success: false, message: error.message };
    return { success: true };
  }, { success: false, message: "Timeout" });
};

export const deleteVoucher = async (id: string): Promise<boolean> => {
  return withTimeout(async () => {
    const { error } = await supabase.from('vouchers').delete().eq('id', id);
    return !error;
  }, false);
};

export const redeemVoucher = async (userId: string, code: string): Promise<{ success: boolean; message: string; user?: User }> => {
  return withTimeout(async () => {
    const { data, error } = await supabase.rpc('redeem_voucher_v2', {
      input_code: code,
      input_user_id: userId
    });

    if (error) return { success: false, message: error.message || 'Database error.' };

    const result = data as { success: boolean; message: string; amount?: number };

    if (result && result.success) {
       const updatedUser = await getCurrentUserProfile(userId);
       const amountMsg = result.amount ? `$${Number(result.amount).toFixed(2)}` : 'credits';
       return { 
         success: true, 
         message: `Added ${amountMsg}!`, 
         user: updatedUser || undefined 
       };
    } else {
       return { success: false, message: result?.message || 'Invalid Code.' };
    }
  }, { success: false, message: "Connection timed out." });
};

// --- Products ---

export const getProducts = async (): Promise<Product[]> => {
  return withTimeout(async () => {
    const { data, error } = await supabase.from('products').select('*');
    if (error) throw error;

    return (data || []).map((p: any) => {
      // 1. Prioritize category_path: "Root:::Category:::Product"
      // Fallback to p.category if category_path is missing
      let fullPath = p.category_path || p.category || 'Apps:::Uncategorized:::Item';
      let parentCategory = 'Apps';
      let subCategory = 'Uncategorized';
      
      const parts = fullPath.split(':::');
      if (parts.length >= 2) {
          parentCategory = parts[0]; // e.g., 'Games' or 'Apps'
          subCategory = parts[1];    // e.g., 'Mobile Legends'
      }

      // Legacy name cleanup (if name contains :::)
      let name = p.name;
      if (name && name.includes(':::')) {
         name = name.split(':::').pop();
      }

      return {
        ...p,
        name: name,
        category: fullPath, // Keep legacy field populated for safety
        category_path: fullPath, 
        parent_category: parentCategory, // Critical for UI Tabs
        sub_category: subCategory,
        price: p.price ?? 0,
        image: p.image || `https://placehold.co/400x400/18181b/ff8c00/png?text=${encodeURIComponent(name || 'Item')}`
      };
    }) as Product[];
  }, []);
};

export const addProduct = async (product: Omit<Product, 'id'>): Promise<{ error?: any } | { success: true }> => {
  return withTimeout(async () => {
    // Explicitly using category_path for the hierarchy
    console.log("Saving Product via Path:", product.category_path);
    
    const payload = {
        name: product.name,
        price: product.price,
        image: product.image,
        // We use category_path column for the structural data
        category_path: product.category_path, 
        // Use the passed category string (usually Group) for legacy/backup compatibility if provided
        // Otherwise fallback to path
        category: product.category || product.category_path,
        // Ensure we don't send a UUID if the DB expects UUID for category_id
        category_id: null, 
    };

    const { error } = await supabase.from('products').insert([payload]);
    
    if (error) {
        console.error("Supabase Insert Error:", error);
        return { error };
    }
    return { success: true };
  }, { error: { message: "Timeout" } as any });
};

export const deleteProduct = async (productId: string) => {
  return withTimeout(async () => {
     const { error } = await supabase.from('products').delete().eq('id', productId);
     if (error) console.error(error);
  }, undefined);
};

// --- Transactions & Purchases ---

export const getTransactions = async (userId?: string): Promise<Transaction[]> => {
  return withTimeout(async () => {
    let query = supabase.from('transactions').select('*').order('created_at', { ascending: false });
    if (userId) query = query.eq('user_id', userId);

    const { data, error } = await query;
    if (error) throw error;
    
    return (data || []).map((t: any) => ({
      ...t,
      description: t.description || t.item_name || (
        t.type === 'purchase' ? 'Shop Purchase' : 
        t.type === 'topup' ? 'Wallet Top-up' :
        t.type === 'deduction' ? 'Admin Deduction' :
        t.type === 'transfer_out' ? 'Sent Funds' :
        t.type === 'transfer_in' ? 'Received Funds' :
        t.type === 'voucher_redemption' ? 'Code Claimed' :
        t.type === 'arcade_win' ? 'Arcade Win' :
        t.type === 'daily_claim' ? 'Daily Claim' :
        t.type === 'cashback' ? 'Perk Cashback' :
        t.type === 'subscription' ? 'Wallet Sub' :
        'Transaction'
      ),
      amount: t.amount ?? 0,
      item_name: t.item_name || t.description || 'Transaction',
      status: t.status || 'completed', // Fallback for old records
      verification_code: t.verification_code // Explicitly map verification_code
    })) as Transaction[];
  }, []);
};

export const updateTransactionStatus = async (txId: string, status: 'completed'): Promise<boolean> => {
  return withTimeout(async () => {
    const { error } = await supabase
      .from('transactions')
      .update({ status: status, created_at: new Date().toISOString() }) // Update time on completion
      .eq('id', txId);
      
    return !error;
  }, false);
};

export const processPurchase = async (userId: string, product: Product, wallet: Wallet): Promise<{ user: User, code: string } | null> => {
  return withTimeout(async () => {
    const currentBalance = wallet.balance ?? 0;
    const price = product.price ?? 0;
    const newBalance = currentBalance - price;
    
    const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
    const cleanName = product.name.substring(0, 10).toUpperCase().replace(/[^A-Z0-9]/g, '');
    const verificationCode = `${cleanName}-#${suffix}`;

    let updatedUser: User | null = null;

    if (wallet.type === 'main') {
        updatedUser = await updateUserBalance(userId, newBalance);
    } else {
        const { error } = await supabase
            .from('wallets')
            .update({ balance: newBalance })
            .eq('id', wallet.id);
        
        if (error) throw error;
        updatedUser = await getCurrentUserProfile(userId);
    }

    if (!updatedUser) return null;

    await supabase.from('transactions').insert([{
      user_id: userId,
      type: 'purchase',
      amount: price,
      created_at: new Date().toISOString(),
      item_name: `Purchased ${product.name}`,
      status: 'pending',
      verification_code: verificationCode,
      wallet_id: wallet.id
    }]);

    const msg = `📦 *NEW ORDER DETECTED*\n\n🛒 *Item:* ${product.name}\n💰 *Price:* $${price.toFixed(2)}\n👤 *User:* ${updatedUser.username}\n👛 *Wallet:* ${wallet.name}\n🔑 *Code:* \`${verificationCode}\``;
    sendTelegramNotification(msg);

    return { user: updatedUser, code: verificationCode };
  }, null);
};

export const processCashback = async (userId: string, walletId: string, amountSpent: number, itemName: string) => {
    const rand = Math.random();
    const rate = rand < 0.15 ? 0.05 : 0.01;
    const cashbackAmount = amountSpent * rate;
    
    if (cashbackAmount <= 0) return;

    const { data: wallet } = await supabase.from('wallets').select('*').eq('id', walletId).single();
    if (!wallet) return;

    const newBalance = (wallet.balance ?? 0) + cashbackAmount;

    await supabase.from('wallets').update({ balance: newBalance }).eq('id', walletId);

    await supabase.from('transactions').insert([{
        user_id: userId,
        type: 'cashback',
        amount: cashbackAmount,
        item_name: `Cashback (${(rate*100).toFixed(0)}%) on ${itemName}`,
        status: 'completed',
        created_at: new Date().toISOString(),
        wallet_id: walletId
    }]);

    const user = await getCurrentUserProfile(userId);
    sendTelegramNotification(`💰 *CASHBACK ALERT*\nUser ${user?.username} just earned $${cashbackAmount.toFixed(2)} cashback on a ${itemName} purchase!`);
};

export const processDeposit = async (userId: string, amount: number) => {
  return withTimeout(async () => {
    const profile = await getCurrentUserProfile(userId);
    if (!profile) return null;

    const newBalance = (profile.balance ?? 0) + amount;
    const updatedUser = await updateUserBalance(userId, newBalance);
    const type = amount >= 0 ? 'topup' : 'deduction';
    const logAmount = Math.abs(amount);

    if (updatedUser) {
      await supabase.from('transactions').insert([{
        user_id: userId,
        type: type,
        amount: logAmount,
        created_at: new Date().toISOString(),
        item_name: amount >= 0 ? 'Balance Top-up' : 'Manual Deduction',
        status: 'completed'
      }]);
    }
    return updatedUser;
  }, null);
};

export const getEmailByUsername = async (username: string): Promise<string | null> => {
  return withTimeout(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('email')
      .eq('username', username)
      .single();
    
    if (error || !data) return null;
    return data.email;
  }, null);
};
